import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Search, Users, UsersRound, Radio } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { EmptyState } from "@/components/sartn/EmptyState";
import { FeatureStatusBanner } from "@/components/sartn/FeatureStatusBanner";
import { cn } from "@/lib/cn";
import { hapticFeedback } from "@/lib/telegram";
import { useLanguageStore } from "@/stores/languageStore";

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.03 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

interface ChatPreview {
  id: number;
  name: string;
  avatarInitial: string;
  avatarColor: string;
  lastMessage: string;
  timestamp: string;
  unreadCount: number;
}

const MOCK_CHATS: ChatPreview[] = [
  { id: 1, name: "Abebe Kebede", avatarInitial: "A", avatarColor: "bg-[var(--color-primary)]", lastMessage: "I sent the payment screenshot", timestamp: "2m", unreadCount: 3 },
  { id: 2, name: "Sara Tesfaye", avatarInitial: "S", avatarColor: "bg-emerald-600", lastMessage: "When will my order arrive?", timestamp: "15m", unreadCount: 1 },
  { id: 3, name: "Dawit Haile", avatarInitial: "D", avatarColor: "bg-violet-600", lastMessage: "Thank you! Great service", timestamp: "1h", unreadCount: 0 },
  { id: 4, name: "Meron Alemu", avatarInitial: "M", avatarColor: "bg-amber-600", lastMessage: "Do you have this in size L?", timestamp: "2h", unreadCount: 0 },
  { id: 5, name: "Yonas Girma", avatarInitial: "Y", avatarColor: "bg-rose-600", lastMessage: "I want to place a bulk order", timestamp: "5h", unreadCount: 2 },
  { id: 6, name: "Tigist Bekele", avatarInitial: "T", avatarColor: "bg-cyan-600", lastMessage: "Can I return the item?", timestamp: "1d", unreadCount: 0 },
];

interface QuickNavItem {
  icon: LucideIcon;
  label: string;
  labelAm: string;
  path: string;
  accentBg: string;
  accentText: string;
}

const QUICK_NAV: QuickNavItem[] = [
  { icon: Users, label: "Contacts", labelAm: "እውቂያዎች", path: "/contacts", accentBg: "bg-blue-50", accentText: "text-blue-600" },
  { icon: UsersRound, label: "Groups", labelAm: "ቡድኖች", path: "/groups", accentBg: "bg-indigo-50", accentText: "text-indigo-600" },
  { icon: Radio, label: "Channels", labelAm: "ቻናሎች", path: "/channels", accentBg: "bg-teal-50", accentText: "text-teal-600" },
];

function localize(en: string, am: string, language: "en" | "am"): string {
  return language === "am" ? am : en;
}

function QuickNavRow() {
  const language = useLanguageStore((state) => state.language);

  return (
    <div className="mb-3 flex gap-2">
      {QUICK_NAV.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.path}
            type="button"
            onClick={() => {
              hapticFeedback("selection");
              window.location.hash = item.path;
            }}
            className="flex flex-1 items-center gap-2 rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-3 shadow-sm transition-transform active:scale-[0.97]"
          >
            <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", item.accentBg)}>
              <Icon className={cn("h-4 w-4", item.accentText)} />
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-[var(--color-text)]">
                {localize(item.label, item.labelAm, language)}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function ChatsPage() {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredChats = MOCK_CHATS.filter((chat) =>
    chat.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const hasChats = filteredChats.length > 0;
  const isSearchEmpty = searchQuery.length > 0 && filteredChats.length === 0;

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title="Chats" />

      <main className="flex-1 px-4 pb-24 pt-2">
        <FeatureStatusBanner
          status="limited"
          title="Preview chats / የሙከራ ቻቶች"
          description="Chat inbox sync is not connected yet. Chat rows are read-only preview data. / የቻት ማመሳሰል ገና አልተገናኘም፤ ዝርዝሩ ለማየት ብቻ ነው።"
        />

        <QuickNavRow />

        <ChatSearchBar value={searchQuery} onChange={setSearchQuery} />

        {!hasChats && !isSearchEmpty && (
          <EmptyState
            icon={MessageCircle}
            title="No chats yet"
            description="Your customer conversations will appear here once you connect your Telegram bot."
          />
        )}

        {isSearchEmpty && (
          <EmptyState
            icon={Search}
            title="No results"
            description={`No chats matching "${searchQuery}"`}
          />
        )}

        {hasChats && (
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="overflow-hidden rounded-[var(--radius-lg)] bg-[var(--color-surface)] shadow-sm"
          >
            {filteredChats.map((chat, index) => (
              <motion.div key={chat.id} variants={fadeUp}>
                <ChatListItem
                  chat={chat}
                  showDivider={index < filteredChats.length - 1}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>
    </div>
  );
}

function ChatSearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative mb-3">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
      <input
        type="text"
        placeholder="Search chats..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-[var(--radius-lg)] bg-[var(--color-surface)]
                   py-2.5 pl-10 pr-4 text-sm shadow-sm
                   text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]
                   outline-none transition-colors
                   focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]"
      />
    </div>
  );
}

function ChatListItem({
  chat,
  showDivider,
}: {
  chat: ChatPreview;
  showDivider: boolean;
}) {
  const language = useLanguageStore((state) => state.language);
  const hasUnread = chat.unreadCount > 0;

  return (
    <button
      type="button"
      disabled
      className={cn(
        "flex w-full cursor-not-allowed items-center gap-3 px-4 py-3 text-left opacity-90",
        showDivider && "border-b border-[var(--color-border-subtle)]",
      )}
      aria-label={`${chat.name} chat preview only`}
    >
      <div
        className={cn(
          "flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
          chat.avatarColor,
        )}
      >
        <span className="font-headline text-sm font-bold text-white">
          {chat.avatarInitial}
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={cn(
              "truncate text-sm font-medium",
              hasUnread
                ? "text-[var(--color-text)]"
                : "text-[var(--color-text-secondary)]",
            )}
          >
            {chat.name}
          </span>
          <span className="shrink-0 text-[10px] text-[var(--color-text-muted)]">
            {chat.timestamp}
          </span>
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <p
            className={cn(
              "truncate text-xs",
              hasUnread
                ? "font-medium text-[var(--color-text-secondary)]"
                : "text-[var(--color-text-muted)]",
            )}
          >
            {chat.lastMessage}
          </p>
          {hasUnread && <UnreadBadge count={chat.unreadCount} />}
        </div>
        <p className="mt-1 text-[10px] font-medium text-amber-700">
          {localize("Read-only preview", "ለማየት ብቻ", language)}
        </p>
      </div>
    </button>
  );
}

function UnreadBadge({ count }: { count: number }) {
  return (
    <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)] px-1.5 text-[10px] font-bold text-white">
      {count}
    </span>
  );
}
